import { useInput } from 'react-admin';
import {
  Stack, Box, Typography, TextField, IconButton, Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { tokens } from '../theme.js';

const DEFAULT_VALUE = {
  stepsEyebrow: '',
  stepsRawTitle: '',
  steps: [],
  docsEyebrow: '',
  docsRawTitle: '',
  docs: [],
};

/**
 * Editor for /admission page copy stored at SiteSettings.admissionContent.
 *
 * Shape:
 *   {
 *     stepsEyebrow, stepsRawTitle,
 *     steps: [{ title, body }, ...],
 *     docsEyebrow, docsRawTitle,
 *     docs: [string, ...],
 *   }
 *
 * Step numbers ("01", "02", …) are auto-generated by the public page
 * from the array order, so the admin only edits title + body.
 */
export function AdmissionContentEditor({ source = 'admissionContent' }) {
  const { field } = useInput({ source });
  const value = field.value && typeof field.value === 'object' ? field.value : DEFAULT_VALUE;
  const steps = Array.isArray(value.steps) ? value.steps : [];
  const docs = Array.isArray(value.docs) ? value.docs : [];

  const patch = (p) => field.onChange({ ...value, ...p });
  const setSteps = (next) => patch({ steps: next });
  const setDocs = (next) => patch({ docs: next });

  const setStep = (i, p) => setSteps(steps.map((s, idx) => (idx === i ? { ...s, ...p } : s)));
  const addStep = () => setSteps([...steps, { title: '', body: '' }]);
  const removeStep = (i) => setSteps(steps.filter((_, idx) => idx !== i));
  const moveStep = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= steps.length) return;
    const next = steps.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setSteps(next);
  };

  const setDoc = (i, v) => setDocs(docs.map((d, idx) => (idx === i ? v : d)));
  const addDoc = () => setDocs([...docs, '']);
  const removeDoc = (i) => setDocs(docs.filter((_, idx) => idx !== i));
  const moveDoc = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= docs.length) return;
    const next = docs.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setDocs(next);
  };

  return (
    <Stack spacing={2}>
      {/* ----- Steps ----- */}
      <Box>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.ink, mb: 0.5 }}>
          Process steps
        </Typography>
        <Typography variant="caption" sx={{ color: tokens.ink3, fontSize: 12, lineHeight: 1.6, display: 'block', mb: 1.5 }}>
          The numbered steps shown under "How it works". Numbers (01, 02…) are auto-generated from the order.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
          <TextField
            size="small"
            label="Section eyebrow"
            value={value.stepsEyebrow || ''}
            onChange={(e) => patch({ stepsEyebrow: e.target.value })}
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            label="Section title"
            value={value.stepsRawTitle || ''}
            onChange={(e) => patch({ stepsRawTitle: e.target.value })}
            helperText='Italic words wrapped in <em>…</em>'
            sx={{ flex: 1 }}
          />
        </Stack>

        <Stack spacing={1}>
          {steps.map((s, i) => (
            <Box
              key={i}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '40px 1fr 2fr auto' },
                gap: 1.5,
                alignItems: 'center',
                p: 1.5,
                border: `1px solid ${tokens.line}`,
                borderRadius: 1,
                background: tokens.paper,
              }}
            >
              <Typography sx={{ fontFamily: 'monospace', color: tokens.ink3, fontSize: 14, textAlign: 'center' }}>
                {String(i + 1).padStart(2, '0')}
              </Typography>
              <TextField
                size="small"
                label="Title"
                value={s.title || ''}
                onChange={(e) => setStep(i, { title: e.target.value })}
              />
              <TextField
                size="small"
                label="Body"
                value={s.body || ''}
                onChange={(e) => setStep(i, { body: e.target.value })}
                multiline
                minRows={1}
              />
              <Stack direction="row" spacing={0.5}>
                <IconButton size="small" onClick={() => moveStep(i, -1)} disabled={i === 0}>
                  <ArrowUpIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => moveStep(i, 1)} disabled={i === steps.length - 1}>
                  <ArrowDownIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => removeStep(i)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          ))}
        </Stack>

        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addStep} sx={{ mt: 1.5 }}>
          Add step
        </Button>
      </Box>

      {/* ----- Documents ----- */}
      <Box sx={{ pt: 2 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.ink, mb: 0.5 }}>
          Documents required
        </Typography>
        <Typography variant="caption" sx={{ color: tokens.ink3, fontSize: 12, lineHeight: 1.6, display: 'block', mb: 1.5 }}>
          The bullet-list under "Documents required". One item per row.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
          <TextField
            size="small"
            label="Section eyebrow"
            value={value.docsEyebrow || ''}
            onChange={(e) => patch({ docsEyebrow: e.target.value })}
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            label="Section title"
            value={value.docsRawTitle || ''}
            onChange={(e) => patch({ docsRawTitle: e.target.value })}
            helperText='Italic words wrapped in <em>…</em>'
            sx={{ flex: 1 }}
          />
        </Stack>

        <Stack spacing={1}>
          {docs.map((d, i) => (
            <Box
              key={i}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 1.5,
                alignItems: 'center',
                p: 1.5,
                border: `1px solid ${tokens.line}`,
                borderRadius: 1,
                background: tokens.paper,
              }}
            >
              <TextField
                size="small"
                label={`Document ${i + 1}`}
                value={d || ''}
                onChange={(e) => setDoc(i, e.target.value)}
              />
              <Stack direction="row" spacing={0.5}>
                <IconButton size="small" onClick={() => moveDoc(i, -1)} disabled={i === 0}>
                  <ArrowUpIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => moveDoc(i, 1)} disabled={i === docs.length - 1}>
                  <ArrowDownIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => removeDoc(i)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          ))}
        </Stack>

        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addDoc} sx={{ mt: 1.5 }}>
          Add document
        </Button>
      </Box>
    </Stack>
  );
}
